import React, { ReactNode, useRef } from 'react';
import { ToolbarSectionItemType } from './menu';
import classNames from 'classnames';
import {
  NeueAddMapBlockStrongIcon,
  LogoBlockStrongIcon,
  NeueContentsListStrongIcon,
  SquiggleStrongIcon,
  FileStrongIcon,
  NeueTableStrongIcon,
  NeueAddStrongIcon,
} from 'src/monet/icons';
import { Nullable } from 'src/types/nullable.type';
import { shallow } from 'zustand/shallow';
import { ContentCreationAddBlockButton } from '../add-block-button';
import { useLogoStore } from '../logo/store';
import { BlockContent } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { useEditorStore } from '../editor-store';
import { IntegrationItems } from './integration-items';
import FilmRegularIcon from 'src/monet/icons/FilmRegularIcon';
import { hasESignatureBlockOrPlaceholderBlock } from '../native-e-signature/utils';

export const CreateToolbarPanel = ({
  showPanel,
  onAddItem,
  onDrag,
}: {
  onAddItem: (item: ToolbarSectionItemType) => void;
  showPanel: boolean;
  onDrag: (node: ReactNode, exceedThreshold: boolean) => void;
}) => {
  const isDraggingSomethingOnEditor = useEditorStore((state) => state.isDraggingSomethingOnEditor, shallow);
  const sections = useEditorStore((state) => state.layout.sections);

  const hasESignatureBlock = !!hasESignatureBlockOrPlaceholderBlock(sections);

  const draggingItemTypeRef = useRef<Nullable<string>>(null);
  const logoState = useLogoStore(
    (state) => ({
      selectedOrgLogo: state.availableOrgLogos[0],
      selectedRecipientLogo: state.availableRecipientLogos[0],
      availableOrgLogos: state.availableOrgLogos,
      orgLogosFetched: state.orgLogosFetched,
      recipientLogosFetched: state.recipientLogosFetched,
      availableRecipientLogos: state.availableRecipientLogos,
    }),
    shallow
  );
  const BlockItems: ToolbarSectionItemType[] = [
    // {
    //   type: 'table',
    //   label: 'Table',
    //   Icon: NeueTableStrongIcon,
    //   content: { type: 'table' },
    // },
    {
      type: 'checklist',
      label: 'Collaborative Checklist',
      Icon: NeueAddMapBlockStrongIcon,
      content: { type: 'mutual-action-plan', uuid: uuidv4().replace(/-/g, '') },
    },
    {
      type: 'cta-button',
      label: 'CTA Button',
      Icon: NeueAddStrongIcon,
      content: {
        type: 'cta-button',
        text: 'Click here',
        backgroundColor: '#F55722',
        textColor: '#FFFFFF',
      },
    },
    {
      type: 'logo',
      label: 'Logo',
      Icon: LogoBlockStrongIcon,
      content: { type: 'logo', orgLogoEnabled: true, recipientLogoEnabled: false, ...logoState },
    },
    {
      type: 'contents-list',
      label: 'Contents List',
      Icon: NeueContentsListStrongIcon,
      content: {
        type: 'contents-list',
        enableIndex: false,
        alignment: 'left',
      },
    },
    {
      type: 'e-signature',
      label: 'e-Signature',
      Icon: SquiggleStrongIcon,
      disabled: hasESignatureBlock,
      tooltip: hasESignatureBlock ? 'Already in your Journey' : undefined,
      content: {
        type: 'placeholder',
        placeholderType: 'add-native-e-signature',
        nativeESignatureContent: {
          highlighted: true,
          pinned: true,
          signees: [],
        },
      },
    },
    {
      type: 'video',
      label: 'Video',
      Icon: FilmRegularIcon,
      content: {
        type: 'placeholder',
        placeholderType: 'add-file',
        fileType: {
          type: 'video',
          label: 'Video',
        },
      },
    },
    {
      type: 'document',
      label: 'Document',
      Icon: FileStrongIcon,
      content: {
        type: 'placeholder',
        placeholderType: 'add-file',
        fileType: {
          type: 'document',
          label: 'Document',
        },
      },
    },
    {
      // Its own entry rather than a variant of 'add-file'. An HTML artifact is
      // an interactive app, not a document, and Danny reaching for "Upload a
      // file" first on QA showed that burying it under a generic file picker
      // makes it undiscoverable. The panel behind this offers upload and paste
      // together.
      type: 'html',
      label: 'HTML',
      Icon: FileStrongIcon,
      content: {
        type: 'placeholder',
        placeholderType: 'add-html',
      },
    },
    {
      type: 'table',
      label: 'Table',
      Icon: NeueTableStrongIcon,
      content: {
        type: 'table',
        value: `
          <table>
            <tbody>
              <tr>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td></td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>`,
      },
    },
  ];

  const renderToolbarSection = (items: ToolbarSectionItemType[]) => {
    return items.map((item) => {
      const { Icon, content, label, type, disabled = false, tooltip } = item;
      const isItemBeingDragged = isDraggingSomethingOnEditor && draggingItemTypeRef.current === item.type;

      const defaultIntegrationContentType = {
        type: 'placeholder',
        placeholderType: 'add-integration',
        integration: { type, label },
      };

      const blockContent = (content || defaultIntegrationContentType) as BlockContent;

      return (
        <ContentCreationAddBlockButton
          tooltip={tooltip}
          disablePointerEvents={!showPanel}
          defaultDraggingClassName='w-full h-10'
          relativePositioning={false}
          disabled={disabled}
          defaultClassName={classNames({
            'fixed w-[12rem] h-10 -left-[96px] opacity-50 -top-[20px] cursor-grabbing': isItemBeingDragged,
            'absolute inset-0': !isDraggingSomethingOnEditor || draggingItemTypeRef.current !== item.type,
          })}
          buttonClassName={classNames('p-0', {
            'pointer-events-auto cursor-grabbing': isItemBeingDragged,
          })}
          blockContent={blockContent}
          onDrop={() => {
            onAddItem({ ...item, content: blockContent });
          }}
          onMouseDown={() => {
            draggingItemTypeRef.current = item.type;
          }}
          onDrag={(...args) => {
            draggingItemTypeRef.current = item.type;
            onDrag(...args);
          }}
        >
          <div className={classNames('flex items-center gap-2 py-[9px] px-2 rounded-lg')}>
            <Icon className='w-5 h-5' />
            <span className='text-neue-journey-medium select-none transform-gpu'>{label}</span>
          </div>
        </ContentCreationAddBlockButton>
      );
    });
  };

  return (
    <div className='w-[248px] max-h-[488px] rounded-lg bg-neue-journey-bg text-neue-journey-fg flex flex-col overflow-y-auto'>
      <div className='flex flex-col'>
        <div className='flex flex-col p-2'>
          {renderToolbarSection(BlockItems)}
          <div className='h-px bg-neue-journey-fg-10 m-2'>&nbsp;</div>
          {renderToolbarSection(IntegrationItems)}
        </div>
      </div>
    </div>
  );
};
